import { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useHistory } from "react-router-dom";

import useApplicantTags from "../../../../components/Events/Event/Applicants/hooks/company/useApplicantTags";
import { normalizeRoleLookupOptions } from "../../../../components/Events/Event/Applicants/eventRequestMappers";
import messages from "../../../../assets/locale/messages";
import { ROUTE_PATHS } from "../../../../utils/RoutesPaths";
import {
  adminPermissionModules,
  lookupsKeys,
} from "../../../../utils/Constants";
import {
  initialFetch,
  resetFilters,
  setApplicantIds,
  setFilters,
  setOrder,
  setPage,
  setRowsCount,
  setSearchValue,
} from "../../../../store/Filter/actions";
import {
  getApprovedCompaniesRequest,
  getCitiesRequest,
  getCountriesRequest,
  getLookupsRequest,
  getSpeakingProficiencyRequest,
  getWorkingShiftRequest,
} from "../../../../store/Lookups/actions";
import {
  fetchImageAsBase64,
  textContainOnlySpaces,
  workHelperShift,
} from "../../../../utils/Helpers";
import logo from "../../../../assets/images/yoram-logo-export-white.svg";
import { downloadProfile } from "../downloadProfile";
import {
  isAdmin,
  isSuperAdmin,
  isVisitor,
  isSupervisorManger,
} from "../../../../utils/Permissions";
import { useAdminModulePermission } from "../../../../utils/adminPortalHelpers";
import { getApplicantListingProfileRequest } from "../../../../store/applicants/actions";
import { getPermissionRequest } from "../../../../store/Auth/actions";
import store from "../../../../store";
import { getApplicantsListColumns } from "../applicantsListColumns";
import {
  getApplicantsStaticFilterOptions,
  hasActiveApplicantsFilters,
  mapApplicantsToTableRows,
} from "../applicantsListHelpers";
import { ADMIN_PORTAL_VARIANT } from "../constants";

const useApplicantsList = () => {
  const dispatch = useDispatch();
  const history = useHistory();
  const lang = useSelector((state) => state.locale.lang);
  const { applicants, shared } = messages[lang];
  const filters = useSelector((state) => state._filters);
  const { cities, countries, speakingProficiency, workingShift, approvedCompanies } =
    useSelector((state) => state.lookups);
  const list = useSelector((state) => state.applicants.list);
  const meta = useSelector((state) => state.applicants.meta);
  const listing = useSelector((state) => state.applicants.listing);
  const isLoading = useSelector((state) => state.loader);
  const { roles, languages, disabilities, chronicDiseases, tags } = useSelector(
    (state) => state.lookups.general,
  );
  const { id: user_id } = store.getState().auth.login.role;
  const { canAdd, canEdit } = useAdminModulePermission(
    adminPermissionModules.applicants,
  );

  const [userBase, setUserBase] = useState(null);
  const [proLineLogo, setProLineLogo] = useState(null);
  const [q, setQuery] = useState("");
  const [rows, setRows] = useState([]);
  const [isDownloading, setIsDownloading] = useState(false);
  const [eventShift, setEventShift] = useState([]);

  const filterOptions = useMemo(
    () => getApplicantsStaticFilterOptions(applicants),
    [applicants],
  );

  const handleFilterChange = (newFilter) => {
    if (list.length === 0) {
      dispatch(setPage(1));
    }
    dispatch(setFilters({ ...filters, ...newFilter }));
  };

  const handlePageChange = (newPage) => {
    dispatch(setPage(newPage));
  };

  const handleRowsCountChange = (newRowsCount) => {
    dispatch(setRowsCount(newRowsCount));
    dispatch(setPage(1));
  };

  const handleEditProfile = (id, e) => {
    e.stopPropagation();
    history.push(ROUTE_PATHS.editApplicantProfile.replace(":id", id));
  };

  const tagOptions = useMemo(
    () => normalizeRoleLookupOptions(tags ?? [], lang),
    [lang, tags],
  );

  const tagControls = useApplicantTags({
    rows,
    selectedIds: [],
    portalVariant: ADMIN_PORTAL_VARIANT,
    refreshList: () => dispatch(initialFetch()),
  });

  const handleTagClick = (row) => {
    tagControls.openApplicantTag(row);
  };

  const handleAddTag = (row, e) => {
    e.stopPropagation();
    tagControls.openApplicantTag(row);
  };

  useEffect(() => {
    (async () => {
      if (list.length > 0) {
        const updatedList = await Promise.all(
          list.map(async (item) => {
            const profileBase64 = item.image
              ? await fetchImageAsBase64(item.image)
              : null;
            return {
              imageBase64: profileBase64,
            };
          }),
        );
        setUserBase(updatedList);
      }
      const _logo = await fetchImageAsBase64(logo);
      setProLineLogo(_logo);
    })();
  }, [list, lang, filters]);

  const updatedSpeaking = speakingProficiency.map((item) => ({
    ...item,
    name: item.label,
  }));

  useEffect(() => {
    if (!isSuperAdmin() && !isAdmin() && !isSupervisorManger()) {
      dispatch(getPermissionRequest({ id: user_id }));
    }
  }, [lang]);

  const canEditTags =
    isSuperAdmin() || isAdmin() || (isVisitor() && (canAdd || canEdit));

  const columns = useMemo(
    () =>
      getApplicantsListColumns({
        applicants,
        shared,
        canEditTags,
        canAdd,
        onAddTag: handleAddTag,
        onTagClick: handleTagClick,
        onEditProfile: handleEditProfile,
      }),
    [applicants, canEditTags, canAdd, lang, shared],
  );

  useEffect(() => {
    return () => {
      sessionStorage.removeItem("applicantsVisibleCols");
      dispatch(resetFilters());
    };
  }, []);

  useEffect(() => {
    setRows(mapApplicantsToTableRows(list, { lang, shared, applicants }));
    const currentSelected = filters.selectedApplicantIds || [];
    const visibleIds = list.map((row) => row.id);
    const selectedButNotVisible = currentSelected.filter(
      (id) => !visibleIds.includes(id),
    );

    if (selectedButNotVisible.length > 0) {
      dispatch(setApplicantIds([...currentSelected]));
    }
  }, [list, lang]);

  useEffect(() => {
    handleFilterChange({ working_shift: "" });
    setEventShift(
      workingShift.map((item) => ({
        ...item,
        name:
          shared.shiftTypes?.[item.shift_type] ||
          workHelperShift(item.shift_type) ||
          item.shift_type,
      })),
    );
  }, [filters.filterRole, workingShift]);

  useEffect(() => {
    dispatch(initialFetch());
  }, [
    filters.page,
    filters.searchValue,
    filters.rowsCount,
    filters.profile_status,
    filters.is_assigned_to_event,
    filters.gender,
    filters.height,
    filters.age,
    filters.weight,
    filters.role_id,
    filters.language_id,
    filters.chronic_disease_id,
    filters.disability_id,
    filters.nationality_id,
    filters.city_id,
    filters.speaking_proficiency,
    filters.working_shift,
    filters.tag_id,
    filters.favourite_company_id,
  ]);

  useEffect(() => {
    if ((q.length >= 3 || q.length === 0) && !textContainOnlySpaces(q)) {
      dispatch(setSearchValue(q));
      handlePageChange(1);
    }
  }, [q]);

  useEffect(() => {
    dispatch(getSpeakingProficiencyRequest());
    dispatch(getCountriesRequest({ page: -1 }));
    dispatch(getApprovedCompaniesRequest());
    dispatch(
      setOrder({
        order_by_first_name_en: lang === "en" ? "asc" : null,
        order_by_first_name_ar: lang === "ar" ? "asc" : null,
      }),
    );
    dispatch(
      getLookupsRequest([
        lookupsKeys.disabilities,
        lookupsKeys.chronicDiseases,
        lookupsKeys.languages,
        lookupsKeys.roles,
        lookupsKeys.tags,
      ]),
    );
    dispatch(
      getCitiesRequest({
        page: -1,
        order_by_name_en: lang === "en" ? "asc" : null,
        order_by_name_ar: lang === "ar" ? "asc" : null,
      }),
    );
  }, [lang]);

  useEffect(() => {
    if (filters.role_id?.id) {
      dispatch(
        getWorkingShiftRequest({
          role_id: filters.role_id?.id ?? null,
        }),
      );
    } else {
      handleFilterChange({ working_shift: "" });
    }
  }, [filters.role_id]);

  const viewApplicantHandler = (event) => {
    if (event.field === "__check__") return;
    history.push(ROUTE_PATHS.viewApplicant.replace(":id", event.row.id));
  };

  const handleSelectionChange = (newSelection) => {
    const previousSelection = filters.selectedApplicantIds || [];

    const addedSelections = newSelection.filter(
      (id) => !previousSelection.includes(id),
    );
    const removedSelections = previousSelection.filter(
      (id) => !newSelection.includes(id),
    );
    let updatedSelection;
    if (addedSelections.length > 0) {
      updatedSelection = [...previousSelection, ...addedSelections];
    } else if (removedSelections.length > 0) {
      updatedSelection = previousSelection.filter(
        (id) => !removedSelections.includes(id),
      );
    } else {
      updatedSelection = previousSelection;
    }
    updatedSelection = Array.from(new Set(updatedSelection));
    dispatch(setApplicantIds(updatedSelection));
  };

  const handleCheckListing = () => {
    if (filters.selectedApplicantIds.length === 0 || isDownloading) return;
    setIsDownloading(true);
    dispatch(
      getApplicantListingProfileRequest({
        user_ids: filters.selectedApplicantIds,
      }),
    );
    dispatch(setApplicantIds([]));
  };

  useEffect(() => {
    const processImages = async () => {
      if (listing.length > 0) {
        const updatedList = await Promise.all(
          listing.map(async (item) => {
            try {
              const profileBase64 = item.image
                ? await fetchImageAsBase64(item.image)
                : null;
              return {
                ...item,
                imageBase64: profileBase64,
              };
            } catch (error) {
              console.error("Error processing image:", error);
              return {
                ...item,
                imageBase64: null,
              };
            }
          }),
        );

        const _logo = await fetchImageAsBase64(logo);
        setProLineLogo(_logo);
        setUserBase(updatedList);
      }
    };
    processImages();
  }, [listing, logo]);

  useEffect(() => {
    const downloadProfiles = async () => {
      // `userBase` is built asynchronously from `listing`; until it catches up it
      // still holds the previous array, which would pair each applicant with the
      // wrong photo.
      const isUserBaseInSync =
        userBase?.length === listing.length &&
        userBase.every((item, index) => item.id === listing[index].id);

      if (
        listing.length > 0 &&
        isUserBaseInSync &&
        proLineLogo !== null &&
        isDownloading
      ) {
        try {
          await downloadProfile(
            userBase,
            applicants,
            lang,
            proLineLogo,
            userBase,
          );
          dispatch(setApplicantIds([]));
          setUserBase(null);
          setProLineLogo(null);
        } catch (error) {
          console.error("Download failed:", error);
        } finally {
          setIsDownloading(false);
        }
      }
    };

    downloadProfiles();
  }, [listing, userBase, proLineLogo, isDownloading]);

  const hasActiveQuery = hasActiveApplicantsFilters(filters);
  const emptyState = hasActiveQuery
    ? applicants.listApplicants.filteredEmptyState
    : applicants.listApplicants.emptyState;

  return {
    applicants,
    searchQuery: q,
    onSearchChange: setQuery,
    filters,
    filterOptions,
    lookupOptions: {
      roles,
      disabilities,
      chronicDiseases,
      cities,
      countries,
      languages,
      updatedSpeaking,
      tags: tagOptions,
      approvedCompanies,
      shared,
    },
    eventShift,
    onFilterChange: handleFilterChange,
    onDownloadProfiles: handleCheckListing,
    showDownloadAction: list.length > 0,
    selectedCount: filters.selectedApplicantIds.length,
    isLoading,
    list,
    rows,
    columns,
    meta,
    emptyState,
    onCellClick: viewApplicantHandler,
    onSelectionChange: handleSelectionChange,
    onPageChange: handlePageChange,
    onPageSizeChange: handleRowsCountChange,
    tagControls,
    tagOptions,
  };
};

export default useApplicantsList;
